import {createSelector} from '@reduxjs/toolkit';
import {isFeatureEnabled} from 'feature-flags';
import {match} from 'ts-pattern';

import {isNil} from 'ramda';

import {useGetAllMakesModelsQuery} from '@dms/api/sales';
import {GetAllMakeModelsApiResponse} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';

import {VehicleCategoryFilter} from '../pages/AllOffers/types/VehicleCategoryFilter';
import {mergeMakesModels} from '../utils/mergeMakesModels';

// Every make & model filter component calls the hook. A module-level selector merges the two
// catalogues once per response for all of them and keeps the lookups built on it stable.
const selectMergedCatalogue = createSelector(
  [
    (carCatalogue: GetAllMakeModelsApiResponse) => carCatalogue,
    (_carCatalogue: GetAllMakeModelsApiResponse, vanCatalogue: GetAllMakeModelsApiResponse) =>
      vanCatalogue,
  ],
  mergeMakesModels
);

/**
 * Makes, model families and models of the vehicle catalogue, which publishes passenger cars and
 * vans separately (Iveco Daily, MAN or the Renault Maxity exist only as vans).
 *
 * - `catalogue` is what the make & model filter offers for the applied vehicle category: passenger
 *   cars, vans, or both when no category applies (all categories, auctions).
 * - `fullCatalogue` holds both, so a selected key keeps its label whatever the category.
 *
 * With `SOURCING_VAN_FILTERS` off the van catalogue is not requested and both are passenger cars.
 */
export function useSourcingMakesModels(vehicleCategory: VehicleCategoryFilter | null) {
  const isVanCatalogueEnabled = isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS);
  const carCatalogue = useGetAllMakesModelsQuery({vehicleType: 'VEHICLETYPE_PASSENGER_CAR'});
  const vanCatalogue = useGetAllMakesModelsQuery(
    {vehicleType: 'VEHICLETYPE_VAN'},
    {skip: !isVanCatalogueEnabled}
  );

  const fullCatalogue =
    isNil(carCatalogue.data) || isNil(vanCatalogue.data)
      ? carCatalogue.data
      : selectMergedCatalogue(carCatalogue.data, vanCatalogue.data);

  const catalogue = match(isVanCatalogueEnabled ? vehicleCategory : null)
    .with(VehicleCategoryFilter.CAR, () => carCatalogue.data)
    .with(VehicleCategoryFilter.VAN, () => vanCatalogue.data)
    .otherwise(() => fullCatalogue);

  return {
    catalogue,
    fullCatalogue,
    isLoading: carCatalogue.isLoading || vanCatalogue.isLoading,
    isError: carCatalogue.isError || vanCatalogue.isError,
  };
}
